import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';

/**
 * TimeInput — combobox-style time picker
 *
 * • Free-text input: the user can type any time ("8:45 AM", "14:30", etc.)
 * • On focus/click a dropdown of 30-min quick-picks appears
 * • Selecting a quick-pick fills the input and closes the dropdown
 * • On blur, the typed value is parsed and normalised; invalid input resets
 * • min prop: only quick-pick slots strictly AFTER that time are shown,
 *   but the user can still type any valid time freely
 *
 * Props:
 *   value       {string}   Current time in 'h:mm A' format (e.g. "8:30 AM")
 *   onChange    {function} Called with 'h:mm A' string when a valid time commits
 *   min         {string}   'h:mm A' — filters the quick-pick list
 *   className   {string}   Extra class on the <input>
 *   placeholder {string}   Input placeholder
 */

const ALL_TIMES = (() => {
	const times = [];
	for (let h = 0; h < 24; h++) {
		for (let m = 0; m < 60; m += 30) {
			times.push(moment({ hour: h, minute: m }).format('h:mm A'));
		}
	}
	return times;
})();

const TIME_FORMATS = ['h:mm A', 'h:mm a', 'h A', 'h a', 'H:mm', 'H', 'HH:mm'];

export function toMinutes(timeStr) {
	if (!timeStr) return -1;
	const m = moment(timeStr, 'h:mm A', true);
	return m.isValid() ? m.hours() * 60 + m.minutes() : -1;
}

function TimeInput({ value, onChange, min, className, placeholder }) {
	const [open, setOpen] = useState(false);
	const [inputVal, setInputVal] = useState(value || '');
	const wrapRef = useRef(null);
	const selectedRef = useRef(null);

	// Keep local input in sync when value changes externally
	useEffect(() => {
		setInputVal(value || '');
	}, [value]);

	const minMinutes = toMinutes(min);
	const quickPicks = minMinutes >= 0
		? ALL_TIMES.filter(t => toMinutes(t) > minMinutes)
		: ALL_TIMES;

	// Commit typed text on blur
	const handleBlur = () => {
		const parsed = moment(inputVal, TIME_FORMATS, false);
		if (parsed.isValid()) {
			const formatted = parsed.format('h:mm A');
			setInputVal(formatted);
			if (formatted !== value) onChange(formatted);
		} else {
			// Reset to last known-good value
			setInputVal(value || '');
		}
		// Delay close so click-on-option fires first
		setTimeout(() => setOpen(false), 150);
	};

	const handleSelect = (time) => {
		setInputVal(time);
		onChange(time);
		setOpen(false);
	};

	// Close on outside click
	useEffect(() => {
		if (!open) return;
		const onOutside = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', onOutside);
		return () => document.removeEventListener('mousedown', onOutside);
	}, [open]);

	// Scroll selected item into view when dropdown opens
	useEffect(() => {
		if (open && selectedRef.current) {
			selectedRef.current.scrollIntoView({ block: 'nearest' });
		}
	}, [open]);

	return (
		<div ref={wrapRef} className={`rfp-time-picker${open ? ' open' : ''}`}>
			<div className="rfp-time-input-wrap">
				<input
					type="text"
					className={`rfp-time-input${className ? ` ${className}` : ''}`}
					value={inputVal}
					placeholder={placeholder || '8:00 AM'}
					onChange={(e) => setInputVal(e.target.value)}
					onFocus={() => setOpen(true)}
					onBlur={handleBlur}
				/>
				<i className="las la-clock rfp-time-icon" />
			</div>
			{open && quickPicks.length > 0 && (
				<div className="rfp-time-picker-dropdown">
					{quickPicks.map(t => (
						<button
							key={t}
							ref={value === t ? selectedRef : null}
							type="button"
							className={`rfp-time-option${value === t ? ' selected' : ''}`}
							// prevent blur before click fires
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => handleSelect(t)}
						>
							{t}
						</button>
					))}
				</div>
			)}
		</div>
	);
}

export default TimeInput;
